<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>
                    mdi-close
                </v-icon>
            </v-btn>
            <h3>Processes</h3>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-form v-model="valid">
                <v-row no-gutters>
                    <v-col cols="12">
                        <v-text-field
                            v-model="process.name"
                            hide-details
                            prepend-icon="mdi-hammer-wrench"
                            label="Name*"
                            :rules="[rules.required]"
                        >
                        </v-text-field>
                    </v-col>
                </v-row>

                <v-row no-gutters>
                    <v-col cols="12">
                        <v-textarea
                            v-model="process.description"
                            prepend-icon="mdi-text"
                            label="Description"
                            hide-details
                        >
                        </v-textarea>
                    </v-col>
                </v-row>
                <v-row no-gutters class="d-flex align-center mt-4 ml-1">
                    <v-simple-checkbox
                        color="primary"
                        v-model="process.assemblyProcess"
                    ></v-simple-checkbox>
                    <p class="ma-0 pa-0">Assembly Process</p>
                </v-row>
                <v-row no-gutters class="d-flex align-center mt-2 ml-1">
                    <v-switch
                        v-model="process.enabled"
                        color="primary"
                        label="Enabled"
                        hide-details
                        class="mt-0"
                    />
                </v-row>
                <v-row no-gutters class="mt-2">
                    <v-col cols="12">
                        <v-autocomplete
                            v-model="process.coordinators"
                            :items="liteUsers"
                            item-text="name"
                            item-value="id"
                            label="Coordinators"
                            prepend-icon="mdi-account-multiple"
                            multiple
                            chips
                            deletable-chips
                            small-chips
                            hide-details
                            clearable
                        />
                    </v-col>
                </v-row>
            </v-form>
            <v-row no-gutters class="mt-3">
                <v-col cols="12">
                    <v-text-field
                        v-model="process.minimumCapacityInMonth"
                        prepend-icon="mdi-weight-kilogram"
                        label="Minimum Capacity Expected In The Month*"
                        :rules="[rules.required]"
                        type="number"
                        min="0"
                    >
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row no-gutters class="mt-3">
                <v-col cols="12">
                    <v-text-field
                        v-model="process.workDays"
                        prepend-icon="mdi-calendar-range"
                        label="Work Days*"
                        type="number"
                        :rules="[rules.required]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row no-gutters class="mt-3">
                <v-col cols="12">
                    <v-text-field
                        v-model="totalMonthlyCapacity"
                        prepend-icon="mdi-calendar-today"
                        label="Minimum Capacity Expected Per Day*"
                        :rules="[rules.required]"
                        type="number"
                        min="0"
                        disabled
                    >
                    </v-text-field>
                </v-col>
            </v-row>

            <v-row no-gutters class="mt-3">
                <v-col cols="12">
                    <v-text-field
                        v-model="process.laborValue"
                        prepend-icon="mdi-calendar-range"
                        label="Labor Value*"
                        :rules="[rules.required]"
                        type="number"
                        min="0"
                    >
                    </v-text-field>
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                color="primary"
                class="mb-4"
                @click="saveProcess"
                rounded
                :loading="loading"
                :disabled="!valid"
            >
                SAVE
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { objDiff } from '@/helpers/objDiff.js'
import moment from 'moment'

export default {
    name: 'ProcessForm',
    props: {
        process: {
            type: Object,
            default: () => ({}),
        },
        originalProcess: {
            type: Object,
            default: () => ({}),
        },
        createForm: {
            type: Boolean,
            default: () => false,
        },
        processSettings: {
            type: Object,
            default: () => ({}),
        },
        liteUsers: {
            type: Array,
            default: () => [],
        },
    },
    data: () => ({
        loading: false,
        typeOptions: ['Production', 'Assembly'],
        valid: false,
        currentMonth: undefined,
        limitDays: undefined,
        rules: {
            required: v => !!v || 'Required',
        },
    }),
    computed: {
        processDiff: function() {
            if (!this.createForm) {
                return objDiff(this.originalProcess, this.process)
            } else {
                return null
            }
        },
        totalMonthlyCapacity() {
            if (!this.process.minimumCapacityInMonth || !this.process.workDays)
                return 0
            return this.process.minimumCapacityInMonth / this.process.workDays
        },
    },
    mounted() {
        try {
            // Get Current Month
            this.currentMonth = moment(new Date()).format('YYYY-MM')

            if (!Array.isArray(this.process.coordinators)) {
                this.$set(this.process, 'coordinators', [])
            }

            if (this.process.enabled === undefined || this.process.enabled === null) {
                this.$set(this.process, 'enabled', true)
            }

            if (!this.createForm) {
                const lastMinCap = this.process.minimumCapacityInMonth
                    ? this.process.minimumCapacityInMonth[this.currentMonth] ??
                      Object.values(this.process.minimumCapacityInMonth).at(-1)
                    : 0

                const lastWorkDays = this.process.workDays
                    ? this.process.workDays[this.currentMonth] ??
                      Object.values(this.process.workDays).at(-1)
                    : 0

                const lastLabor = this.process.laborValue
                    ? this.process.laborValue[this.currentMonth] ??
                      Object.values(this.process.laborValue).at(-1)
                    : 0

                this.$set(this.process, 'minimumCapacityInMonth', lastMinCap)
                this.$set(this.process, 'workDays', lastWorkDays)
                this.$set(this.process, 'laborValue', lastLabor)
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async saveProcess() {
            if (this.createForm) {
                await this.create()
            } else {
                await this.update()
            }
        },
        async create() {
            try {
                this.loading = true
                const process = await API.createProcess({
                    settingId: this.processSettings.id,
                    ...this.process,
                })
                this.$emit('addProcess', process)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async update() {
            try {
                this.loading = true
                const diff = { ...this.processDiff }
                // objDiff doesn't handle arrays correctly; always send the full coordinators array
                diff.coordinators = Array.isArray(this.process.coordinators)
                    ? this.process.coordinators
                    : []
                const process = await API.updateProcess({
                    settingId: this.processSettings.id,
                    processId: this.process.id,
                    ...diff,
                })
                this.$emit('replaceProcess', process)
                this.$emit('close')
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
    },
}
</script>

<style></style>
